import React from 'react';
import { History, X, TrendingUp, TrendingDown, Clock, User, Sparkles, AlertCircle, Activity } from 'lucide-react';
import { useCostHistory } from '../hooks/useInventory';
import type { CostHistoryItem } from '../api/inventory.client';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

interface Props {
  item: {
    id: string | number;
    name: string;
    sku?: string;
    unit?: string;
    cost?: number;
  } | null;
  onClose: () => void;
}

export const CostHistoryModal: React.FC<Props> = ({ item, onClose }) => {
  if (!item) return null;

  const { data: history, isLoading, isError } = useCostHistory(item.id);

  const formatCurrency = (val: number | null | undefined) => {
    if (val === null || val === undefined) return '$0';
    return `$${Number(val).toLocaleString('es-CO')}`;
  };

  const formatDate = (isoStr: string) => {
    try {
      const d = new Date(isoStr);
      return d.toLocaleDateString('es-CO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return isoStr;
    }
  };

  const chartData = React.useMemo(() => {
    if (!history) return [];
    return [...history].reverse().map(item => {
      const d = new Date(item.created_at);
      return {
        date: d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' }),
        cost: Number(item.new_cost),
        fullDate: formatDate(item.created_at),
        reason: item.reason,
      };
    });
  }, [history]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div 
        className="w-full max-w-xl rounded-2xl bg-surface border border-divider shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-divider px-6 py-4 bg-surface-muted/50">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <History size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-[color:var(--color-text)]">
                  Historial de Precios de Costo
                </h3>
                {item.sku && (
                  <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-md">
                    {item.sku}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted font-medium">{item.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-muted hover:bg-surface-muted hover:text-[color:var(--color-text)] transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Current Cost Banner */}
        <div className="px-6 py-3 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-muted uppercase tracking-wider">Costo Actual Registrado</p>
            <p className="text-xl font-extrabold text-primary">
              {formatCurrency(item.cost)}{' '}
              <span className="text-xs font-normal text-muted">/ {item.unit || 'unidad'}</span>
            </p>
          </div>
          <div className="text-right">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-surface border border-divider text-muted shadow-sm">
              <Clock size={13} className="text-primary" />
              {history ? `${history.length} cambio${history.length === 1 ? '' : 's'}` : 'Cargando...'}
            </span>
          </div>
        </div>

        {/* Timeline Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading ? (
            <div className="py-12 text-center text-muted space-y-2">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto" />
              <p className="text-sm font-medium">Cargando historial de cambios...</p>
            </div>
          ) : isError ? (
            <div className="py-10 text-center text-red-500 space-y-2">
              <AlertCircle size={32} className="mx-auto opacity-70" />
              <p className="text-sm font-medium">Error al cargar el histórico de costos.</p>
            </div>
          ) : !history || history.length === 0 ? (
            <div className="py-12 text-center text-muted border border-dashed border-divider rounded-2xl p-6">
              <History size={40} className="mx-auto mb-3 opacity-20 text-primary" />
              <p className="text-sm font-semibold text-[color:var(--color-text)]">Sin variaciones de costo registradas</p>
              <p className="text-xs text-muted max-w-sm mx-auto mt-1">
                A partir de ahora, cada vez que modifiques el precio de costo o recibas una orden de compra, quedará registrado aquí automáticamente.
              </p>
            </div>
          ) : (
            <>
              {/* Gráfico de Variación */}
              {chartData.length > 1 && (
                <div className="mb-6 rounded-2xl border border-divider bg-surface p-4 shadow-sm">
                  <div className="mb-4 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-muted">
                    <Activity size={14} className="text-primary" /> Tendencia de Costo
                  </div>
                  <div className="h-40 w-full text-xs">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-divider)" opacity={0.5} />
                        <XAxis dataKey="date" tick={{ fill: 'var(--color-text-muted)' }} tickLine={false} axisLine={false} minTickGap={15} />
                        <YAxis tickFormatter={(val) => `$${val}`} tick={{ fill: 'var(--color-text-muted)' }} tickLine={false} axisLine={false} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: 'var(--color-surface)', borderRadius: '8px', border: '1px solid var(--color-divider)', fontSize: '12px' }}
                          formatter={(value: number) => [`$${value.toLocaleString('es-CO')}`, 'Costo']}
                          labelFormatter={(label, payload) => payload?.[0]?.payload?.fullDate || label}
                        />
                        <Line type="monotone" dataKey="cost" stroke="var(--color-primary)" strokeWidth={3} dot={{ r: 4, fill: 'var(--color-primary)', strokeWidth: 0 }} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-0.5 before:bg-divider">
                {history.map((record: CostHistoryItem, idx: number) => {
                const isInitial = record.old_cost === null || record.old_cost === undefined;
                const diff = !isInitial ? Number(record.new_cost) - Number(record.old_cost) : 0;
                const pct = !isInitial && Number(record.old_cost) > 0 
                  ? ((diff / Number(record.old_cost)) * 100).toFixed(1) 
                  : '0';

                return (
                  <div key={record.id} className="relative group">
                    {/* Timeline bullet */}
                    <div 
                      className={`absolute -left-6 top-1 h-3.5 w-3.5 rounded-full border-2 border-surface ${
                        idx === 0 
                          ? 'bg-primary ring-4 ring-primary/20' 
                          : isInitial 
                            ? 'bg-blue-400' 
                            : diff > 0 
                              ? 'bg-amber-500' 
                              : 'bg-emerald-500'
                      }`} 
                    />

                    {/* Card */}
                    <div className="rounded-xl border border-divider bg-surface p-4 shadow-sm hover:shadow transition">
                      <div className="flex items-start justify-between gap-3">
                        {/* Price change comparison */}
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {isInitial ? (
                              <span className="text-base font-bold text-[color:var(--color-text)]">
                                {formatCurrency(record.new_cost)}
                              </span>
                            ) : (
                              <div className="flex items-center gap-2 text-sm font-semibold">
                                <span className="line-through text-muted font-normal">
                                  {formatCurrency(record.old_cost)}
                                </span>
                                <span className="text-muted">→</span>
                                <span className="text-base font-bold text-[color:var(--color-text)]">
                                  {formatCurrency(record.new_cost)}
                                </span>
                              </div>
                            )}

                            {/* Badge */}
                            {isInitial ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-bold text-blue-600 dark:text-blue-400 border border-blue-500/20">
                                <Sparkles size={10} /> Costo Inicial
                              </span>
                            ) : diff > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-600 dark:text-amber-400 border border-amber-500/20">
                                <TrendingUp size={10} /> +{pct}% (+{formatCurrency(diff)})
                              </span>
                            ) : diff < 0 ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                <TrendingDown size={10} /> {pct}% ({formatCurrency(diff)})
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-muted/20 px-2 py-0.5 text-[10px] font-semibold text-muted">
                                Sin variación
                              </span>
                            )}
                          </div>

                          {/* Reason */}
                          <p className="mt-1.5 text-xs text-[color:var(--color-text)] font-medium">
                            {record.reason || 'Actualización de costo'}
                          </p>
                        </div>

                        {/* Date */}
                        <div className="text-right shrink-0">
                          <p className="text-[11px] font-medium text-muted">
                            {formatDate(record.created_at)}
                          </p>
                          {record.user?.name && (
                            <p className="mt-0.5 text-[10px] text-muted flex items-center justify-end gap-1">
                              <User size={10} /> {record.user.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-divider px-6 py-3 bg-surface-muted/30 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-full bg-surface-muted px-5 py-2 text-xs font-bold text-[color:var(--color-text)] border border-divider hover:bg-surface transition shadow-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
